import React, { useState } from 'react';
import { Input } from './../ui/input';
import { Badge } from './../ui/badge';
import { Button } from '../ui/button';
import { Search, X, Check } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './../ui/popover';

const avatarColors = {
  blue: 'from-blue-500 to-cyan-500',
  purple: 'from-purple-500 to-pink-500',
  green: 'from-emerald-500 to-teal-500',
  orange: 'from-orange-500 to-amber-500',
  red: 'from-red-500 to-rose-500',
  indigo: 'from-indigo-500 to-blue-500'
};

export default function MemberSelector({ members, selectedIds = [], onChange }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const activeMembers = members.filter(m => m.active);
  const selectedMembers = activeMembers.filter(m => selectedIds.includes(m.id));
  
  const filteredMembers = activeMembers.filter(member => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      member.name?.toLowerCase().includes(query) ||
      member.profession?.toLowerCase().includes(query)
    );
  });

  const handleToggleMember = (memberId) => {
    if (selectedIds.includes(memberId)) {
      onChange(selectedIds.filter(id => id !== memberId));
    } else {
      onChange([...selectedIds, memberId]);
    }
  };

  const handleRemoveMember = (memberId) => {
    onChange(selectedIds.filter(id => id !== memberId));
  };

  return (
    <div className="space-y-2">
      {/* Selected Members */}
      {selectedMembers.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
          {selectedMembers.map((member) => {
            const colorGradient = avatarColors[member.avatar_color] || avatarColors.blue;
            return (
              <Badge
                key={member.id}
                variant="secondary"
                className="bg-white shadow-sm border border-slate-200 pl-2 pr-1 py-1.5 gap-2"
              >
                <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center`}>
                  <span className="text-xs font-bold text-white">
                    {member.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="font-medium text-slate-700">{member.name}</span>
                <button
                  onClick={() => handleRemoveMember(member.id)}
                  className="ml-1 hover:bg-slate-200 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Selector Dropdown */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal hover:bg-slate-50"
          >
            <Search className="w-4 h-4 mr-2 text-slate-400" />
            {selectedMembers.length === 0 ? (
              <span className="text-slate-500">Selecionar responsáveis...</span>
            ) : (
              <span className="text-slate-700">
                {selectedMembers.length} {selectedMembers.length === 1 ? 'responsável' : 'responsáveis'} {selectedMembers.length === 1 ? 'selecionado' : 'selecionados'}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar membros..."
                className="pl-9"
              />
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {filteredMembers.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                {search ? 'Nenhum membro encontrado' : 'Nenhum membro ativo'}
              </div>
            ) : (
              <div className="py-2">
                {filteredMembers.map((member) => {
                  const isSelected = selectedIds.includes(member.id);
                  const colorGradient = avatarColors[member.avatar_color] || avatarColors.blue;
                  
                  return (
                    <button
                      key={member.id}
                      onClick={() => handleToggleMember(member.id)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors
                        ${isSelected ? 'bg-blue-50' : ''}
                      `}
                    >
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-md ring-2 ${isSelected ? 'ring-blue-300' : 'ring-white'}`}>
                        <span className="text-sm font-bold text-white">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-slate-900">{member.name}</div>
                        <div className="text-xs text-slate-500">{member.profession}</div>
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}